/**
 * Codemine Logo Component
 * Animated, responsive logo for brand consistency
 */

import React from 'react';

interface CodemineLogoProps {
  size?: number;
  animated?: boolean;
  pulsing?: boolean;
  className?: string;
}

export const CodemineLogo: React.FC<CodemineLogoProps> = ({ 
  size = 32, 
  animated = true,
  pulsing = false,
  className = '' 
}) => {
  return (
    <div 
      className={`codemine-logo ${animated ? 'hover:scale-110' : ''} ${pulsing ? 'logo-pulse' : ''} transition-transform duration-300 ${className}`}
      style={{ width: size, height: size }}
    >
      <svg 
        viewBox="0 0 100 100" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full"
      >
        {/* Background Circle with Gradient */}
        <defs>
          <linearGradient id="codemine-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6C63FF" />
            <stop offset="50%" stopColor="#8B7FFF" />
            <stop offset="100%" stopColor="#A259FF" />
          </linearGradient>
          
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Outer Circle */}
        <circle
          cx="50"
          cy="50"
          r="48"
          fill="url(#codemine-gradient)"
          filter="url(#glow)"
        />

        {/* Code Brackets */}
        <g transform="translate(50, 50)">
          {/* Left Bracket */}
          <path
            d="M -15 -20 L -25 -20 L -25 -12 L -18 0 L -25 12 L -25 20 L -15 20"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            opacity="0.95"
          />
          
          {/* Right Bracket */}
          <path
            d="M 15 -20 L 25 -20 L 25 -12 L 18 0 L 25 12 L 25 20 L 15 20"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            opacity="0.95"
          />
          
          {/* Center Slash */}
          <path
            d="M 5 -25 L -5 25"
            stroke="white"
            strokeWidth="3.5"
            strokeLinecap="round"
            opacity="0.95"
          />
        </g>

        {/* Decorative Dots (Binary inspired) */}
        <circle cx="20" cy="20" r="2" fill="white" opacity="0.6" />
        <circle cx="80" cy="20" r="2" fill="white" opacity="0.6" />
        <circle cx="20" cy="80" r="2" fill="white" opacity="0.6" />
        <circle cx="80" cy="80" r="2" fill="white" opacity="0.6" />
      </svg>

      <style>{`
        .logo-pulse {
          animation: logo-pulse 2s ease-in-out infinite;
        }

        @keyframes logo-pulse {
          0%, 100% {
            transform: scale(1);
            filter: drop-shadow(0 0 8px rgba(108, 99, 255, 0.4));
          }
          50% {
            transform: scale(1.05);
            filter: drop-shadow(0 0 16px rgba(108, 99, 255, 0.6));
          }
        }
      `}</style>
    </div>
  );
};

/**
 * Simple Logo Icon (for small spaces)
 */
export const CodemineLogoSimple: React.FC<Omit<CodemineLogoProps, 'animated'>> = ({ 
  size = 24, 
  pulsing = false,
  className = ''
}) => {
  return (
    <div 
      className={`codemine-logo-simple ${pulsing ? 'logo-pulse' : ''} ${className}`}
      style={{ width: size, height: size }}
    >
      <svg 
        viewBox="0 0 100 100" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full"
      >
        <defs>
          <linearGradient id="simple-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6C63FF" />
            <stop offset="100%" stopColor="#A259FF" />
          </linearGradient>
        </defs>

        {/* Simplified Code Symbol */}
        <g transform="translate(50, 50)">
          {/* Left Angle Bracket */}
          <path
            d="M -10 -15 L -20 0 L -10 15"
            stroke="url(#simple-gradient)"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          
          {/* Right Angle Bracket */}
          <path
            d="M 10 -15 L 20 0 L 10 15"
            stroke="url(#simple-gradient)"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </g>
      </svg>
    </div>
  );
};

/**
 * Logo with Text (for headers)
 */
export const CodemineLogoWithText: React.FC<{
  size?: 'sm' | 'md' | 'lg';
  theme?: 'dark' | 'light';
  animated?: boolean;
}> = ({ 
  size = 'md', 
  theme = 'dark',
  animated = true 
}) => {
  const sizes = {
    sm: { logo: 24, text: 'text-lg' },
    md: { logo: 32, text: 'text-xl' },
    lg: { logo: 40, text: 'text-2xl' }
  };

  const { logo, text } = sizes[size];
  const textColor = theme === 'dark' ? 'text-white' : 'text-gray-900';

  return (
    <div className="flex items-center gap-3">
      <CodemineLogo size={logo} animated={animated} />
      <div className="flex flex-col leading-tight">
        <span className={`font-bold ${text} ${textColor} tracking-tight`}>
          Codemine
        </span>
        <span className="text-xs text-gray-400 font-medium">
          AI Code Generator
        </span>
      </div>
    </div>
  );
};

/**
 * Animated Loading Logo (for splash screens)
 */
export const CodemineLoadingLogo: React.FC<{ size?: number }> = ({ size = 80 }) => {
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative">
        <CodemineLogo size={size} pulsing={true} />
        
        {/* Rotating Ring */}
        <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s' }}>
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <circle
              cx="50"
              cy="50"
              r="48"
              fill="none"
              stroke="url(#codemine-gradient)"
              strokeWidth="2"
              strokeDasharray="10 20"
              opacity="0.5"
            />
          </svg>
        </div>
      </div>
      
      <div className="flex gap-1">
        <div className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0ms' }}></div>
        <div className="w-2 h-2 rounded-full bg-indigo-500 animate-bounce" style={{ animationDelay: '150ms' }}></div>
        <div className="w-2 h-2 rounded-full bg-purple-500 animate-bounce" style={{ animationDelay: '300ms' }}></div>
      </div>
      
      <span className="text-sm font-medium text-gray-400 animate-pulse">
        Loading...
      </span>
    </div>
  );
};

export default CodemineLogo;
